// ** React Imports
import { useState, useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'src/utils/i18n'
import { motion, AnimatePresence } from 'framer-motion'

// ** Next Import
import { useRouter } from 'next/router'

// ** MUI Imports
import Box from '@mui/material/Box'
import List from '@mui/material/List'

import Badge from '@mui/material/Badge'
import Drawer from '@mui/material/Drawer'
import MuiAvatar from '@mui/material/Avatar'
import ListItem from '@mui/material/ListItem'
import Typography from '@mui/material/Typography'
import ListItemText from '@mui/material/ListItemText'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemButton from '@mui/material/ListItemButton'

// ** Third Party Components
import PerfectScrollbar from 'react-perfect-scrollbar'

import { useDispatch, useSelector } from 'react-redux'
import { fetchUserProfile, switchUser, selectChat } from 'src/store/apps/whatsapp'

// ** Icon Imports
import ICon from 'src/@core/components/icon'

// ** Custom Components Import

import Image from 'next/image'
import { Icon } from '@iconify/react'
import NoData from 'src/components/NoData'
import { changeExtremeService } from 'src/store/apps/Common'

const TextMessagesSideBar = props => {

  // ** Props
  const {
    store,
    hidden,
    mdAbove,
    dispatch,
    selectMessage,
    sidebarWidth,
    leftSidebarOpen,
    handleLeftSidebarToggle,
    calls,
    setCalls,
    headImg,
    setHaedImg,
    setChat,
    setContacts,
    contacts,
    setLeftScrollTop,
    leftScrollTop,
    advanced
  } = props

  // ** States
  const [canLoad, setCanLoad] = useState(false)
  const [active, setActive] = useState(null)

  useEffect(() => {
    if (!canLoad) {
      if (store && store.list.length > 0) {
        setCanLoad(true)
        setActive(store.list[0].PK)
      }
    }
  }, [store, canLoad])

  //从进入这个js开始，只加载一次

  // ** Hooks
  const router = useRouter()

  const handleChatClick = item => {
    if (item.PK === active) {
      if (!mdAbove && advanced) {
        handleLeftSidebarToggle()
      }
    } else {
      setActive(item.PK)
      if (advanced) { // 开通高级服务
        if (domRef.current) setLeftScrollTop(domRef.current.scrollTop)
        dispatch(selectMessage(item))
        localStorage.setItem('whatsappItem', JSON.stringify(item))
        handleLeftSidebarToggle()
      }
    }
  }

  const [currentTime, setCurrentTime] = useState('')
  useEffect(() => {
    const updateTime = () => {
      const list = store.list // 假设 store.list 已经定义并包含数据

      // 获取当前日期
      const now = new Date()
      const currentYear = now.getFullYear()
      const currentMonth = now.getMonth() + 1 // JavaScript 中月份从 0 开始
      const currentDate = now.getDate()

      // 遍历列表中的时间戳
      const formattedTimestamps = list.map(item => {
        const date = new Date(item.snippetTimestamp)

        // 获取时间戳的年、月、日
        const year = date.getFullYear()
        const month = date.getMonth() + 1
        const day = date.getDate()

        let formattedDate = ''

        if (year === currentYear) {
          if (month === currentMonth && day === currentDate) {
            // 当年份、月份和日期都相同时，设置为 HH:mm:ss
            formattedDate = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(
              2,
              '0'
            )}:${String(date.getSeconds()).padStart(2, '0')}`
          } else {
            // 当年份相同但月份或日期不同，设置为 MM-DD
            formattedDate = `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}`
          }
        } else {
          // 当年份不同，设置为 YYYY-MM-DD
          formattedDate = `${year}/${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}`
        }

        return formattedDate
      })

      // 将所有格式化后的日期拼接成一个字符串并设置为 currentTime
      setCurrentTime(formattedTimestamps)
    }

    updateTime() // 初次渲染时更新时间
  }, [store.list])

  const domRef = useRef()
  const [scroll, setScroll] = useState(0)

  useEffect(() => {
    if (!domRef.current) return

    const dom = domRef.current
    const func = () => {
      const clientHeight = dom.clientHeight
      const scrollTop = dom.scrollTop
      const scrollHeight = dom.scrollHeight
      const progress = (scrollTop / (scrollHeight - clientHeight) * 100).toFixed(0)
      setScroll(progress)
    }

    dom.addEventListener('scroll', func)

    return () => {
      dom.removeEventListener('scroll', func)
    }
  }, [canLoad, leftSidebarOpen])

  useEffect(() => {
    if (scroll >= 100 && advanced) dispatch(fetchUserProfile())
  }, [scroll])

  const renderChats = useMemo(() => {
    if (store) {
      dispatch(changeExtremeService(store.advanced === true || store.advanced === 'true')) // 更新高级服务状态

      if (store.list && store.list.length) {
        return store.list.map((item, index) => {
          return (
            <ListItem key={index} disablePadding sx={{ '&:not(:last-child)': { mb: 1.5 }, borderRadius: '10px' }}>
              <ListItemButton
                disableRipple
                onClick={() => handleChatClick(item)}
                sx={{
                  px: 2.5,
                  py: 2.5,
                  borderRadius: '8px',
                  width: '100%',
                  borderRadius: 1,
                  alignItems: 'flex-start',
                  ...(item.PK === active && { backgroundColor: theme => `${theme.palette.primary.main} !important` })
                }}
              >
                <ListItemAvatar sx={{ m: 0 }}>
                  <Badge
                    overlap='circular'
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right'
                    }}
                  >
                    <MuiAvatar
                      src={item.avata != '' ? item.avata : item.defaultAvata}
                      sx={{
                        width: 40,
                        height: 40,
                        outline: theme => `2px solid ${item.PK === active ? theme.palette.common.white : 'transparent'}`
                      }}
                    />
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  sx={{
                    my: 0,
                    ml: 4,
                    mr: 1.5,
                    '& .MuiTypography-root': { ...(item.PK === active && { color: 'common.white' }) }
                  }}
                  primary={
                    <Typography noWrap sx={{ ...(item.PK !== active ? { color: 'text.secondary' } : {}) }}>
                      {item.chatid}
                    </Typography>
                  }
                  secondary={
                    <Typography noWrap variant='body2' sx={{ ...(item.PK !== active && { color: 'text.disabled' }) }}>
                      {item.snippet}
                    </Typography>
                  }
                />
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    flexDirection: 'column',
                    justifyContent: 'flex-start'
                  }}
                >
                  <Typography
                    sx={{
                      whiteSpace: 'nowrap',
                      color: item.PK === active ? 'common.white' : '#808080',
                      fontSize: '10px'
                    }}
                  >
                    <>{currentTime[index]}</>
                  </Typography>
                </Box>
              </ListItemButton>
            </ListItem>
          )
        })
      } else {
        return (
          <ListItem>
            <Typography sx={{ color: 'text.secondary', width: '100%', opacity: '0.7', textAlign: 'center' }}>
              <NoData />
            </Typography>
          </ListItem>
        )
      }
    }
  }, [store.list, active])

  useEffect(() => {
    if (leftSidebarOpen && domRef.current) domRef.current.scrollTop = leftScrollTop
  }, [leftSidebarOpen])

  return (
    <>
      {canLoad ? (
        <div>
          <Drawer
            open={leftSidebarOpen}
            onClose={handleLeftSidebarToggle}
            variant={mdAbove ? 'permanent' : 'temporary'}
            ModalProps={{
              disablePortal: true,
              keepMounted: false // Better open performance on mobile.
            }}
            sx={{
              zIndex: 7,
              height: '100%',
              display: 'block',
              position: mdAbove ? 'static' : 'absolute',
              '& .MuiDrawer-paper': {
                boxShadow: 'none',
                overflow: 'hidden',
                width: sidebarWidth,
                position: mdAbove ? 'static' : 'absolute'
              },
              '& > .MuiBackdrop-root': {
                borderRadius: 1,
                position: 'absolute',
                zIndex: theme => theme.zIndex.drawer - 1
              },
              '& .MuiPaper-root': {
                borderTopRightRadius: {
                  xs: '10px', // 小于900px时
                  md: '0px' // 大于等于900px时
                },
                borderBottomRightRadius: {
                  xs: '10px', // 小于900px时
                  md: '0px' // 大于等于900px时
                },
                borderTopLeftRadius: '10px',
                borderBottomLeftRadius: '10px'
              }
            }}
            className={` ${advanced ? 'border-r-[1px]' : ''}`}
          >
            <div>
              <div className='flex items-center justify-between px-4 pt-3'>
                <Image
                  src='/images/whatsapp/whatsapp.svg'
                  width={10}
                  height={10}
                  alt='logo'
                  className='w-[30px] h-[30px]'
                />
                {/* <Icon icon='mingcute:contacts-fill' style={{ color: '#999999' }} width={25} height={25} /> */}
              </div>

              {/* 搜索框 */}
            </div>

            <div className='h-full'>
              <Box
                sx={{
                  p: theme => theme.spacing(2, 3, 0),
                  '&::-webkit-scrollbar': {
                    width: '6px' // 设置滚动条的宽度
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: '#f0f0f0' // 滚动条的背景色
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#999', // 滚动条的滑块颜色
                    borderRadius: '10px' // 滑块的圆角
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    backgroundColor: '#666' // 当鼠标悬停在滑块上时的颜色
                  },
                  height: 'calc(100% - 50px)'
                }}
                className={`${advanced ? 'overflow-auto' : 'overflow-hidden'}`}
                ref={domRef}
              >
                <List sx={{ mb: 7.5, p: 0, mt: 4 }} className={`h-full ${!advanced ? 'blur-[16px] select-none' : ''}`}>
                  {renderChats}
                </List>
              </Box>
            </div>
          </Drawer>
        </div>
      ) : (
        <div></div>
      )}
    </>
  )
}

export default TextMessagesSideBar
